import { DatePipe, AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, OnDestroy, input, inject, signal, computed, effect } from '@angular/core';
import { AdvancedSettingService, ClientService, StaffStoreService, TimeconversionService, ToastMessageService } from '@fitness-force/api';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { ClientGender, ClientRelation, ClientStatus } from '@fitness-force/enum';
import type { ClientById, Country, STAFFINTERFACE, STAGE_MASTER_MODEL } from '@fitness-force/models';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { ChangeSalesRepresentativeComponent, ClientHistoryComponent, DayAgoPipe, timeLineService } from '@fitness-force/shared';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO, STAGE_MASTER_STATE, COUNTRY_MASTER_STATE_LIST } from '@fitness-force/state';
import { BadgeModule } from 'primeng/badge';
import { MessageModule } from 'primeng/message';
import { PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { BehaviorSubject, Observable, combineLatest, map, of, Subject } from 'rxjs';
import { catchError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { ClientCreditBalanceComponent } from '../client-credit-balance/client-credit-balance.component';
import { AvatarModule } from 'primeng/avatar';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators, FormBuilder, FormGroup } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CountryISO, NgxIntlTelInputComponent, PhoneNumberFormat } from '@fitness-force/fitness-int-phone-code';
import * as lpn from 'google-libphonenumber';

@Component({
  selector: 'fitness-force-client-profile-details-tab',
  templateUrl: './client-profile-details-tab.component.html',
  styleUrls: ['./client-profile-details-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SkeletonModule,
    PopoverModule,
    CurrencyformatPipe,
    DateTimeFormatPipe,
    ClientCreditBalanceComponent,
    MessageModule,
    BadgeModule,
    AvatarModule,
    DayAgoPipe,
    ChangeSalesRepresentativeComponent,
    FormsModule,
    ToggleSwitchModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    DatePickerModule,
    ReactiveFormsModule,
    NgxIntlTelInputComponent,
    ClientHistoryComponent,
    TranslateModule, AsyncPipe, NgClass],
})
export class ClientProfileDetailsTabComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly clientService = inject(ClientService);
  private readonly store = inject(Store);
  private readonly timeService = inject(TimeconversionService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly staffStoreService = inject(StaffStoreService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly advanceSettingService = inject(AdvancedSettingService);
  private readonly datePipe = inject(DatePipe);

  readonly client = input<ClientById>({} as ClientById);
  readonly tenantId = input(0);
  readonly showSkeleton$ = input(new BehaviorSubject<boolean>(true));

  // Signals for component state
  readonly currentTenantId = signal(0);
  readonly showClientDetails = signal(false);
  readonly showClientSidebar = signal(false);
  readonly allTenants = signal<any[]>([]);
  readonly selectedTenant = signal<any>(null);
  readonly clientIds = signal<number[]>([]);
  readonly showOrganization = signal(false);
  readonly showAddCreditButton = signal(false);
  readonly currentTenantClient = signal(true);
  readonly showBookAppointmentPopup = signal(false);
  readonly attachmentsModal = signal(false);
  readonly showCreditBalance = signal(false);
  readonly changeMobile = signal(false);
  readonly changeEmail = signal(false);
  readonly changeDOB = signal(false);
  readonly changeGender = signal(false);
  readonly changeEmergencyContact = signal(false);
  readonly changeAddress = signal(false);
  readonly changeSalesRep = signal(false);
  readonly changeChannel = signal(false);
  readonly changeSource = signal(false);
  readonly showEcEdit = signal(false);
  readonly showClientHistory = signal(false);

  // Collapsible section states - when score exists: score expanded, others collapsed; when score null: score collapsed, others expanded
  readonly scoreSectionExpanded = signal(true);
  readonly clientDetailsExpanded = signal(true);
  readonly marketingPrefExpanded = signal(true);

  // Legacy BehaviorSubjects for backward compatibility
  readonly allClients$ = new BehaviorSubject<ClientById>({} as ClientById);
  readonly toggleSendEmailDialog$ = new BehaviorSubject<boolean>(false);
  readonly toggleCreateSmsDialog$ = new BehaviorSubject<boolean>(false);
  readonly isBlackListed = new BehaviorSubject<boolean>(false);
  readonly toggleChangeSaleRepDialog$ = new BehaviorSubject<boolean>(false);
  readonly toggleEditProfileDialog$ = new BehaviorSubject<boolean | null>(false);

  // Loading state for client details reload
  readonly isLoadingClientDetails = signal(false);

  selectedMenu: any;
  getAccessTenantList$: Observable<any> | any | null;

  readonly genderOptions = signal<string[]>([]);
  private static readonly DEFAULT_GENDER_OPTIONS: string[] = Object.entries(ClientGender)
    .filter(([, v]) => typeof v === 'number')
    .map(([key]) => key);
  private static readonly MARKETING_PREFERENCE_CHANNEL_BY_FORM_KEY: Record<string, string> = {
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    phone: 'Phone',
    push: 'Push',
  };
  ClientRelation = Object.entries(ClientRelation)
    .filter((key) => typeof key[1] != 'number')
    .map(([key, value]) => ({ key, value }));

  channelCollection: any[] = CONSTANTS_DATA.CHANNEL_SOURCES;
  updateClientFormGroup: FormGroup = new FormGroup({});
  isClientRepresentative = new BehaviorSubject(true);
  claims: Observable<string[]> = of(['can_be_client_rep']);
  readonly Source = signal<string[]>([]);
  readonly onlyCountries = signal<CountryISO[]>([]);
  readonly tenantCountry = signal<any>(null);
  readonly PhoneNumberFormat = PhoneNumberFormat;
  readonly canEditDetails = signal(false);
  readonly clientMobileNoCountry = signal('');
  readonly countries = signal<Country[]>([]);
  readonly phoneUtil = lpn.PhoneNumberUtil.getInstance();

  // Signal to track skeleton state — synced from showSkeleton$ BehaviorSubject in ngOnInit
  readonly showSkeletonState = signal(true);

  // Add destroy subject for cleanup
  private readonly destroy$ = new Subject<void>();
  public currentSalesRep: STAFFINTERFACE | null = null;


  constructor() {
    this.initializeForm();

    combineLatest([this.store.select(TENANTS_INFO), this.store.select(COUNTRY_MASTER_STATE_LIST)]).subscribe(([tenant, country]) => {
      this.currentTenantId.set(tenant.selected_tenant.id);
      this.allTenants.set(tenant.all_tenant);
      this.selectedTenant.set(tenant.selected_tenant);
      this.countries.set(country);
    });

    // Watch for client signal changes and initialize form values
    effect(() => {
      const client = this.client();
      if (client && client.id) {
        this.initializeFormValues();
      }
    });

    // Set initial collapse/expand state based on score presence
    effect(() => {
      const score = this.client()?.score;
      if (score) {
        this.scoreSectionExpanded.set(true);
        this.clientDetailsExpanded.set(false);
        this.marketingPrefExpanded.set(false);
      } else {
        this.scoreSectionExpanded.set(false);
        this.clientDetailsExpanded.set(true);
        this.marketingPrefExpanded.set(true);
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to showSkeleton$ BehaviorSubject to sync with local signal
    // This ensures OnPush change detection picks up skeleton state changes
    const skeletonSubject = this.showSkeleton$();
    if (skeletonSubject && !skeletonSubject.closed) {
      skeletonSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.showSkeletonState.set(value);
          this.cd.detectChanges();
        });
    }

    combineLatest([
      this.featureFlagService.getFlag$('client_organisation_link'),
      this.featureFlagService.getFlag$('show_refund')
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([clientOrgLink, showRefund]) => {
        this.showOrganization.set(clientOrgLink);
        this.showAddCreditButton.set(showRefund);
        this.cd.detectChanges();
      });

    this.setTenantCountry();
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant) => {
        const tid = tenant.selected_tenant.id;
        if (tid > 0) {
          this.loadClientGenderOptions(tid);
        }
      });
  }

  private resolveGenderFormValue(clientGender: string | number | null | undefined): string | null {
    if (clientGender == null || clientGender === '') return null;
    const strVal = typeof clientGender === 'string' ? clientGender : (ClientGender[clientGender as ClientGender] ?? null);
    const options = this.genderOptions();
    return (strVal && options.length > 0 && options.includes(strVal)) ? strVal : (options[0] ?? strVal ?? null);
  }

  private loadClientGenderOptions(tenantId: number): void {
    this.advanceSettingService.getAdvanceSetting(tenantId, 'gender_allowed')
      .pipe(takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe({
        next: (setting) => {
          const options = ClientProfileDetailsTabComponent.parseGenderOptions(setting?.value);
          this.genderOptions.set(options);
          const client = this.client();
          if (client?.id && this.updateClientFormGroup.get('gender')) {
            this.updateClientFormGroup.get('gender')?.setValue(this.resolveGenderFormValue(client.gender));
          }
          this.cd.detectChanges();
        },
        error: () => {
          this.genderOptions.set(ClientProfileDetailsTabComponent.DEFAULT_GENDER_OPTIONS);
          this.cd.detectChanges();
        }
      });
  }

  private static parseGenderOptions(value: string | null | undefined): string[] {
    if (!value?.trim()) {
      return ClientProfileDetailsTabComponent.DEFAULT_GENDER_OPTIONS;
    }
    const mapped = value.split(',')
      .map(v => ClientGender[Number(v.trim()) as ClientGender])
      .filter((name): name is string => !!name);
    return mapped.length > 0 ? mapped : ClientProfileDetailsTabComponent.DEFAULT_GENDER_OPTIONS;
  }


  initializeForm() {
    this.updateClientFormGroup = this.fb.group({
      marketing_preferences: this.fb.group({
        email: [false],
        sms: [false],
        whatsapp: [false],
        phone: [false],
        push: [false]
      }),
      // Add other client fields here
      mobile_number: [''],
      email_id: ['', [Validators.email, Validators.required]],
      date_of_birth: [''],
      emergency_contact_person: [''],
      emergency_contact_number: [''],
      gender: [''],
      channel: [''],
      source: [''],
      customSource: [''],
      address: [''],
      salesRep: ['']
    });
  }

  initializeFormValues() {
    const client = this.client();
    if (client) {
      // Initialize marketing preferences
      const marketingPrefs = {
        email: client.promotional_notification_channels_allowed?.includes('Email'),
        sms: client.promotional_notification_channels_allowed?.includes('SMS'),
        whatsapp: client.promotional_notification_channels_allowed?.includes('WhatsApp'),
        phone: client.promotional_notification_channels_allowed?.includes('Phone'),
        push:
          client.promotional_notification_channels_allowed?.includes('Push') ||
          client.promotional_notification_channels_allowed?.includes('MobileNotification')
      };

      const source = this.channelCollection.find(c => c.channel === this.client().channel)?.source ?? [];
      let updatedSource = [...source];

      // Handle custom source logic
      if (updatedSource.length > 0) {
        // Ensure "Others" is in the list
        if (!updatedSource.includes('Others')) {
          updatedSource = [...updatedSource, 'Others'];
        }

        // If client has a source that is not in the list, prepend it
        if (client.source && !updatedSource.includes(client.source)) {
          updatedSource = [client.source, ...updatedSource];
        }
      }
      this.Source.set(updatedSource);

      // Get sales rep first, then patch the form - use takeUntil to prevent memory leaks
      const salesRepId = client.client_representative_id || 0;
      if (salesRepId && salesRepId > 0) {
        this.getSalesRep(salesRepId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (salesRep) => {
              if (salesRep?.id) {
                this.currentSalesRep = salesRep;
                this.patchFormValues(marketingPrefs, salesRep);
              } else {
                this.currentSalesRep = null;
                this.patchFormValues(marketingPrefs, null);
              }
            },
            error: (error) => {
              console.error('Error loading sales rep:', error);
              this.currentSalesRep = null;
              this.patchFormValues(marketingPrefs, null);
            }
          });
      } else {
        this.currentSalesRep = null;
        this.patchFormValues(marketingPrefs, null);
      }
    }
  }

  private patchFormValues(marketingPrefs: any, salesRep: STAFFINTERFACE | null) {
    const client = this.client();
    this.updateClientFormGroup.patchValue({
      marketing_preferences: marketingPrefs,
      mobile_number: client.mobile_number,
      email_id: client.email_id,
      date_of_birth: this.isDefaultDate(client.date_of_birth) ? null : this.timeService.ConvertUTCToLocalDateTime(client.date_of_birth),
      emergency_contact_person: client.emergency_contact_person,
      emergency_contact_number: client.emergency_contact_number,
      gender: this.resolveGenderFormValue(this.client().gender),
      channel: this.channelCollection.find(c => c.channel == this.client().channel),
      source: this.Source().length > 0 ? this.Source().find(s => s == this.client().source) : null,
      customSource: '',
      address: client.address,
      salesRep: salesRep
    });

    if (!this.isCurrentTenantClient) {
      this.updateClientFormGroup.get('marketing_preferences')?.disable();
    }
    this.updateClientFormGroup.updateValueAndValidity();

    if (client.mobile_country_code) {
      const country = this.countries().find(o => o.dial_code.replace(/\D/g, "") == this.client().mobile_country_code.replace(/\D/g, ""));
      if (country) {
        this.clientMobileNoCountry.set(country.two_letter_code.toLowerCase());
      }
    }

    this.cd.detectChanges();
  }

  onMarketingPreferenceChange() {
    const marketingPrefs = this.updateClientFormGroup.get('marketing_preferences')?.value;
    const updatedPreferences = Object.entries(marketingPrefs)
      .filter(([, enabled]) => enabled)
      .map(([key]) => ClientProfileDetailsTabComponent.MARKETING_PREFERENCE_CHANNEL_BY_FORM_KEY[key])
      .filter((channel): channel is string => !!channel);

    const clientObj: Array<{ op: string, path: string, value: any }> = [];
    clientObj.push({ op: "replace", path: "/PromotionalNotificationChannelsAllowed", value: updatedPreferences });

    const tenantId = this.tenantId();
    const clientId = this.client().id;
    this.clientService.patchClient(clientId, tenantId, clientObj).subscribe({
      next: () => {
        this.client().promotional_notification_channels_allowed = updatedPreferences;
        this.toastMessageService.showSuccess(this.translate.instant('CLIENT_PROFILE.SUCCESS_MESSAGES.MARKETING_PREFERENCES_UPDATED'));
        this.cd.detectChanges();
      },
      error: (error) => {
        this.toastMessageService.showError(error);
        this.initializeFormValues();
        this.cd.detectChanges();
      }
    });
  }

  getClientDetails() {
    const clientId = this.client()?.id;
    if (clientId) {
      this.isLoadingClientDetails.set(true);
      this.showSkeleton$().next(true);
      this.clientService.getClientByIdNew(clientId).subscribe({
        next: (res: ClientById) => {
          this.allClients$.next(res);
          this.isLoadingClientDetails.set(false);
          this.showSkeleton$().next(false);
          this.cd.detectChanges();
        },
        error: () => {
          this.isLoadingClientDetails.set(false);
          this.showSkeleton$().next(false);
          this.cd.detectChanges();
        }
      });
    }
  }

  setTenantCountry() {
    combineLatest([this.store.select(TENANTS_INFO), this.store.select(COUNTRY_MASTER_STATE_LIST)]).subscribe(([tenant, country]) => {
      this.currentTenantId.set(tenant.selected_tenant.id);
      const countries = country;
      if (countries.length > 0) {
        const foundCountry = countries.find(o => o.three_letter_code.toLowerCase() == tenant.selected_tenant.country.toLowerCase());
        if (foundCountry) {
          this.tenantCountry.set(foundCountry.two_letter_code.toLowerCase());
        }
        const countryCodes: CountryISO[] = [];
        for (const [, value] of Object.entries(CountryISO)) {
          countryCodes.push(<CountryISO>value);
        }
        this.onlyCountries.set(countryCodes);
        this.cd.detectChanges();
      }
      else {
        throw new Error('Country Not Found');
      }
    })
  }

  readonly isCurrentTenantClient = computed(() => {
    const client = this.client();
    const currentTenantId = this.currentTenantId();
    const clientStatus = client?.client_status;
    if (client && (clientStatus == ClientStatus.Client || clientStatus?.toString() == ClientStatus[ClientStatus.Client])) {
      return currentTenantId == client.home_club_tenant_id;
    }
    if (client && (clientStatus == ClientStatus.Prospect || clientStatus?.toString() == ClientStatus[ClientStatus.Prospect])) {
      return currentTenantId == client.latest_createdin_tenant_id;
    }
    else {
      if (client && (currentTenantId == client.latest_createdin_tenant_id && currentTenantId == client.home_club_tenant_id)) {
        return true;
      }
      else if (client && (currentTenantId == client.home_club_tenant_id)) {
        return true;
      }
      else {
        return false;
      }
    }
  });

  clientStatus(client: any): string {
    if (client?.client_status)
      return this.clientService.getClientStatusHtml(client.client_status.toString());
    else
      return '';
  }

  memberSince(fromDate: string) {
    return timeLineService(this.timeService.ConvertUTCToLocalDateTime(fromDate));
  }

  getStageStatus(): Observable<string> {
    const client = this.client();
    const stageMaster = client?.stage_master?.find(s => s.tenant_id === this.currentTenantId());
    return this.store.select(STAGE_MASTER_STATE).pipe(
      map((value: STAGE_MASTER_MODEL[]) => {
        const stage = value.find(obj => obj.id == stageMaster?.stage_master_id);
        return stage?.name_primary_language || '';
      })
    );
  }

  isDefaultDate(date: string): boolean {
    const year = new Date(date).getFullYear();
    if (year == 9999 || year == 1 || date == null) return true;
    else return false;
  }

  isDefaultBirthDayDate(date: string): boolean {
    if (date == null) return true;
    const year = new Date(date).getFullYear();
    if (year == 9999 || year == 1) return true;
    else return false;
  }

  isProspect(status: any): boolean {
    if (status == null) return false;
    const cltStatus = ClientStatus[status as keyof typeof ClientStatus];
    return cltStatus == ClientStatus.Prospect || status.toString() == ClientStatus[ClientStatus.Prospect]
  }

  isNonMember(status: any): boolean {
    if (status == null) return false;
    const cltStatus = ClientStatus[status as keyof typeof ClientStatus];
    return cltStatus == ClientStatus.ClientNonMember || status.toString() == ClientStatus[ClientStatus.ClientNonMember]
  }

  isExClientNonMember(status: any): boolean {
    if (status == null) return false;
    const cltStatus = ClientStatus[status as keyof typeof ClientStatus];
    return cltStatus == ClientStatus.ExClientNonMember || status.toString() == ClientStatus[ClientStatus.ExClientNonMember]
  }


  crownType(clientSince: string) {
    const ValidDate = new Date(this.timeService.ConvertUTCToLocalDateTime(clientSince));
    if (isNaN(ValidDate.getDate())) {
      return { crown: 'silver-crown', coin: 'less-spent-icon', color: 'text-400' };
    }
    if (this.isDefaultDate(clientSince)) {
      return { crown: 'default', coin: 'default', color: '' };
    }
    const duration = timeLineService(this.timeService.ConvertUTCToLocalDateTime(clientSince));
    if (
      duration.timeline == 'year' &&
      duration.value >= 1 &&
      duration.value < 3 &&
      duration.type == 'ago'
    ) {
      return { crown: 'bronze-crown', coin: 'medium-spent-icon', color: 'text-800' };
    } else if (
      duration.timeline == 'year' &&
      duration.value >= 3 &&
      duration.value < 5 &&
      duration.type == 'ago'
    ) {
      return { crown: 'silver-crown', coin: 'less-spent-icon', color: 'text-400' };
    } else if (
      duration.timeline == 'year' &&
      duration.value >= 5 &&
      duration.type == 'ago'
    ) {
      return { crown: 'gold-crown', coin: 'most-spent-icon', color: 'text-yellow-600' };
    } else {
      return { crown: 'default', coin: 'default', color: '' };
    }
  }

  readonly showSkeleton = computed(() => this.showSkeletonState());

  get clientDetailsObj(): BehaviorSubject<ClientById> {
    return new BehaviorSubject<ClientById>(this.client());
  }

  changeInAction(_e: any) {
    this.getClientDetails();
  }


  ActiveTenant(client: any) {
    if (!client || typeof client !== 'object') return null;
    const tenants = this.allTenants() ?? [];
    const clientStatus = client?.client_status;
    if (clientStatus == ClientStatus.Prospect || clientStatus?.toString() == ClientStatus[ClientStatus.Prospect]) {
      return tenants.find((o: { id: number; }) => o.id == client?.latest_createdin_tenant_id) ?? null;
    }
    if (client?.home_club_tenant_id && client.home_club_tenant_id > 0) {
      return tenants.find((o: { id: number; }) => o.id == client.home_club_tenant_id) ?? null;
    }
    return tenants.find((o: { id: number; }) => o.id == client?.latest_createdin_tenant_id) ?? null;
  }

  getSalesRep(staffId: number) {
    return this.staffStoreService.getStaffById(staffId).pipe(map((staff) => {
      return staff;
    }));
  }

  GetReffferedBy(staffId: number) {
    return this.staffStoreService.getStaffById(staffId).pipe(map((staff) => {
      return staff;
    }));
  }

  getLanguage(code: string) {
    return CONSTANTS_DATA.ALL_LANGUAGES.find((lang) => (lang.LANG_CODE === code))?.LANG_ABBRE ?? '';
  }

  toggleSendEmailDialog() {
    this.toggleSendEmailDialog$.next(true);
  }

  closeSendEmailDialog(event: any) {
    this.toggleSendEmailDialog$.next(false);
    if (event?.action === 'updated') this.getClientDetails();
  }

  toggleCreateSmsDialog() {
    this.toggleCreateSmsDialog$.next(true);
  }

  closeCreateSmsDialog(event: any) {
    this.toggleCreateSmsDialog$.next(false);
    if (event?.action === 'updated') this.getClientDetails();
  }

  toggleChangeSaleRepDialog() {
    this.toggleChangeSaleRepDialog$.next(true);
  }

  closeChangeSaleRepDialog(event: any) {
    this.toggleChangeSaleRepDialog$.next(false);
    if (event?.action === 'updated') {
      setTimeout(() => {
        this.getClientDetails();
        this.cd.detectChanges();
      }, 500);
    }
  }

  onUpdatedRepresentative(event: any) {
    const salesRepId = event || 0;
      if (salesRepId && salesRepId > 0) {
        this.getSalesRep(salesRepId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (salesRep) => {
              if (salesRep?.id) {
                this.currentSalesRep = salesRep;
                this.updateClientFormGroup.get('salesRep')?.setValue(salesRep); 
              }
            },
            error: (error) => {
              console.error('Error loading sales rep:', error);
              this.currentSalesRep = null;
              this.updateClientFormGroup.get('salesRep')?.setValue(null);
            }
          });
      }
  }


  showCreditBalancePopup() {
    this.showCreditBalance.set(this.showAddCreditButton());
  }

  get isClientBlackListed() {
    const client = this.client();
    return client && client.is_blacklisted;
  }

  get clientDetails(): BehaviorSubject<ClientById> {
    return new BehaviorSubject<ClientById>(this.client());
  }

  get NewRepresentative(): UntypedFormControl {
    return this.updateClientFormGroup.get('NewRepresentative') as UntypedFormControl;
  }


  CloseBookAppointment(event: any) {
    this.showBookAppointmentPopup.set(event);
  }

  viewWaiver() {
    this.attachmentsModal.set(true);
  }

  closeAttachmentDialog(_event: any) {
    this.attachmentsModal.set(false);
  }

  reloadEvents(_event: any) {
    // Placeholder for future implementation
  }

  closeCreditPopup(event: any) {
    this.showCreditBalance.set(false);
    if (event?.action === 'updated') this.getClientDetails();
  }

  getLastCenter(tenantId: number) {
    return this.allTenants().find((o: { id: number; }) => o.id == tenantId);
  }

  updateField(fieldName: string, value: any) {
    const clientObj: Array<{ op: string, path: string, value: any }> = [];
    let successMessageKey = '';

    switch (fieldName) {
      case 'mobile_number':
        clientObj.push({ op: "replace", path: "/MobileNumber", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.MOBILE_UPDATED';
        break;
      case 'email_id':
        clientObj.push({ op: "replace", path: "/EmailId", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.EMAIL_UPDATED';
        break;
      case 'date_of_birth':
        clientObj.push({ op: "replace", path: "/DateOfBirth", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.DOB_UPDATED';
        break;
      case 'emergency_contact_person':
      case 'emergency_contact_number':
        clientObj.push({ op: "replace", path: "/EmergencyContactPerson", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.EMERGENCY_CONTACT_UPDATED';
        break;
      case 'gender':
        clientObj.push({ op: "replace", path: "/Gender", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.GENDER_UPDATED';
        break;
      case 'channel':
        clientObj.push({ op: "replace", path: "/Channel", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.CHANNEL_UPDATED';
        break;
      case 'source':
        clientObj.push({ op: "replace", path: "/Source", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.SOURCE_UPDATED';
        break;
      case 'address':
        clientObj.push({ op: "replace", path: "/Address", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.ADDRESS_UPDATED';
        break;
      case 'client_representative_id':
        clientObj.push({ op: "replace", path: "/ClientRepresentativeId", value: value });
        successMessageKey = 'CLIENT_PROFILE.SUCCESS_MESSAGES.SALES_REP_UPDATED';
        break;
    }

    const tenantId = this.tenantId();
    this.clientService.patchClient(this.client().id, tenantId, clientObj).subscribe({
      next: () => {
        const client = this.client();
        if (fieldName in client) {
            if (fieldName == 'mobile_number') {
            const mobileObj = this.updateClientFormGroup.get('mobile_number')?.value;
            client.mobile_number = mobileObj.number.replace(/[\s\-()]/g, '');
            client.mobile_country_code = mobileObj.dialCode;
            this.updateClientFormGroup.get('mobile_number')?.setValue(client.mobile_number);
            this.clientMobileNoCountry.set(mobileObj.countryCode);
          }
          else {
            (client as any)[fieldName] = value;
            if (fieldName === 'source') {
              this.updateClientFormGroup.patchValue({ source: value });
            }
          }

        }
        if (successMessageKey) {
          this.toastMessageService.showSuccess(this.translate.instant(successMessageKey));
        }
        this.resetEditMode(fieldName);
        this.cd.detectChanges();

      },
      error: (error) => {
        console.error(`Error updating ${fieldName}:`, error);
        this.toastMessageService.showError(error);
        const client = this.client();
        if (fieldName in client) {
          const originalValue = (client as any)[fieldName];
          this.updateClientFormGroup.patchValue({ [fieldName]: originalValue });
        }
        this.resetEditMode(fieldName);
        this.cd.detectChanges();
      }
    });
  }

  resetEditMode(fieldName: string) {
    switch (fieldName) {
      case 'mobile_number':
        this.changeMobile.set(false);
        break;
      case 'email_id':
        this.changeEmail.set(false);
        break;
      case 'date_of_birth':
        this.changeDOB.set(false);
        break;
      case 'emergency_contact_person':
      case 'emergency_contact_number':
        this.changeEmergencyContact.set(false);
        break;
      case 'gender':
        this.changeGender.set(false);
        break;
      case 'channel':
        this.changeChannel.set(false);
        break;
      case 'source':
        this.changeSource.set(false);
        break;
      case 'address':
        this.changeAddress.set(false);
        break;
      case 'client_representative_id':
        this.changeSalesRep.set(false);
        break;
    }
  }

  // Individual field update handlers
  onMobileNumberUpdate() {
    const value = this.updateClientFormGroup.get('mobile_number')?.value ? this.updateClientFormGroup.get('mobile_number')?.value.number.replace(/[\s\-()]/g, '') : 0;
    this.updateField('mobile_number', value);
  }

  onEmailUpdate() {
    const value = this.updateClientFormGroup.get('email_id')?.value;
    this.updateField('email_id', value);
  }

  onDOBUpdate() {
    const value = this.updateClientFormGroup.get('date_of_birth')?.value;
    this.updateField('date_of_birth', this.datePipe.transform(value, 'yyyy-MM-dd'));
  }

  onEmergencyContactUpdate() {
    const person = this.updateClientFormGroup.get('emergency_contact_person')?.value;
    const number = this.updateClientFormGroup.get('emergency_contact_number')?.value ? this.updateClientFormGroup.get('emergency_contact_number')?.value.e164Number : 0;

    const clientObj: Array<{ op: string, path: string, value: any }> = [
      { op: "replace", path: "/EmergencyContactPerson", value: person },
      { op: "replace", path: "/EmergencyContactNumber", value: number }
    ];

    this.clientService.patchClient(this.client().id, this.tenantId(), clientObj).subscribe({
      next: () => {
        // Update emergency contact data if needed
        this.changeEmergencyContact.set(false);
        this.cd.detectChanges();
      },
      error: (error) => {
        this.updateClientFormGroup.patchValue({
          emergency_contact_person: this.client().emergency_contact_person,
          emergency_contact_number: this.client().emergency_contact_number
        });
        this.toastMessageService.showError(error);
        this.cd.detectChanges();
      }
    });
  }

  onGenderUpdate() {
    const value = this.updateClientFormGroup.get('gender')?.value;
    this.updateField('gender', typeof value === 'string' ? value : value?.value);
  }

  onChannelUpdate() {
    const value = this.updateClientFormGroup.get('channel')?.value;
    let source = value.source || [];

    // Ensure "Others" is in the list for new channel selection
    if (source.length > 0 && !source.includes('Others')) {
      source = [...source, 'Others'];
    }
    this.Source.set(source);

    this.updateField('channel', value.channel);
  }

  onSourceUpdate() {
    const value = this.updateClientFormGroup.get('source')?.value;
    if (value === 'Others') {
      // Do nothing, wait for custom input
      this.updateClientFormGroup.get('customSource')?.setValue('');
    } else {
      this.updateField('source', value);
    }
  }

  onCustomSourceUpdate() {
    const value = this.updateClientFormGroup.get('customSource')?.value;
    if (value) {
      this.updateField('source', value);
    }
  }

  onAddressUpdate() {
    const value = this.updateClientFormGroup.get('address')?.value;
    this.updateField('address', value);
  }

  onSalesRepUpdate() {
    const value = this.updateClientFormGroup.get('salesRep')?.value;
    this.updateField('salesRep', value);
  }

  // Helper method to handle enter key press
  onEnterKeyPress(event: KeyboardEvent, fieldName: string) {
    if (event.key === 'Enter') {
      event.preventDefault();
      switch (fieldName) {
        case 'mobile_number':
          this.onMobileNumberUpdate();
          break;
        case 'email_id':
          this.onEmailUpdate();
          break;
        case 'address':
          this.onAddressUpdate();
          break;
        // Add other cases as needed
      }
    }
  }

  private getParsedNumber(phoneNumber: string, countryCode: string): string {
    let number: lpn.PhoneNumber = '' as unknown as lpn.PhoneNumber;
    let num: string = '';
    try {
      number = this.phoneUtil.parse(phoneNumber, countryCode.toUpperCase());
      num = this.phoneUtil.format(number, lpn.PhoneNumberFormat.NATIONAL);
    } catch (e) { 
      console.error("Error parsing phone number:", e);
    }
    return num;
  }

  toggleClientHistory() {
    this.showClientHistory.update(value => !value);
    this.cd.detectChanges();
  }

  toggleScoreSection() {
    this.scoreSectionExpanded.update(v => !v);
  }

  toggleClientDetailsSection() {
    this.clientDetailsExpanded.update(v => !v);
  }

  toggleMarketingPrefSection() {
    this.marketingPrefExpanded.update(v => !v);
  }

  /** Returns gauge stroke-dashoffset for semicircle (0-100 score) */
  getGaugeOffset(score: number): number {
    const clamped = Math.min(100, Math.max(0, score));
    return 141.4 * (1 - clamped / 100);
  }

  /** Returns display score: uses score when churn_score is null (e.g. lead type) */
  getDisplayScore(scoreObj: { score?: number | null; churn_score?: number | null } | undefined | null): number {
    if (!scoreObj) return 0;
    const val = scoreObj.churn_score ?? scoreObj.score ?? 0;
    return typeof val === 'number' ? val : 0;
  }

  /** Returns translation key for score section title based on score_type (lead, churn, etc.) */
  getScoreSectionTitleKey(scoreType: string | undefined): string {
    const t = (scoreType ?? '').toLowerCase();
    if (t === 'lead') return 'CLIENT_PROFILE.SCORE.LEAD_SCORE';
    if (t === 'churn') return 'CLIENT_PROFILE.SCORE.CHURN_RISK';
    return 'CLIENT_PROFILE.SCORE.SCORE_DETAILS';
  }

  /** Parses contributing_factors - may be plain strings or JSON strings */
  parseContributingFactors(factors: string[] | undefined): string[] {
    if (!factors?.length) return [];
    return factors.map((f) => {
      if (typeof f !== 'string') return String(f);
      const trimmed = f.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (typeof parsed === 'object' && parsed !== null) {
            return Object.entries(parsed)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ');
          }
        } catch {
          return f;
        }
      }
      return f;
    });
  }

  /** Parses recommended_actions - may be plain string or JSON array string */
  parseRecommendedActions(actions: string | undefined): string[] {
    if (!actions?.trim()) return [];
    const trimmed = actions.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((a) => (typeof a === 'string' ? a : String(a)));
        }
      } catch {
        return [trimmed];
      }
    }
    return [trimmed];
  }

  /** Returns score category label and color classes for gauge/card based on score_category */
  getScoreCategoryStyle(category: string | undefined): { labelKey: string; gaugeColor: string; cardBg: string; textColor: string } {
    const c = (category ?? '').toLowerCase();
    if (c === 'high') return { labelKey: 'CLIENT_PROFILE.SCORE.HIGH_SCORE', gaugeColor: '#ef4444', cardBg: 'rgba(239, 68, 68, 0.08)', textColor: 'text-red-600' };
    if (c === 'medium') return { labelKey: 'CLIENT_PROFILE.SCORE.MEDIUM_SCORE', gaugeColor: '#ea580c', cardBg: 'rgba(234, 88, 12, 0.08)', textColor: 'text-orange-600' };
    if (c === 'low') return { labelKey: 'CLIENT_PROFILE.SCORE.LOW_SCORE', gaugeColor: '#16a34a', cardBg: 'rgba(22, 163, 74, 0.08)', textColor: 'text-green-600' };
    return { labelKey: 'CLIENT_PROFILE.SCORE.SCORE_CATEGORY', gaugeColor: '#6b7280', cardBg: 'var(--p-surface-100)', textColor: 'text-600' };
  }

  closeClientHistoryDialog(_event: any) {
    this.showClientHistory.set(false);
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to unsubscribe from all observables
    this.destroy$.next();
    this.destroy$.complete();
  }
}